import {Component, computed, effect, model, input, inject} from '@angular/core';
import {ReactiveFormsModule, FormGroup} from '@angular/forms';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {ProjectStepSaveButtonComponent} from '../../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {TableModule} from 'primeng/table';
import {TranslocoPipe} from '@jsverse/transloco';
import {AppCurrencyPipe, IntegrationSetupFeeResponse} from '@frontend/shared';
import {FormControlsConfig} from '../../../step-8-1-proposal-parameter/components/abstract-form-directive';
import {CommentModalButtonComponent} from '../../../../../../../../shared/components/dialog/comment-modal-button/comment-modal-button-reactive/comment-modal-button.component';
import {IntegrationSetupFeesContainer} from './integration-setup-fees.types';
import {toSignal} from '@angular/core/rxjs-interop';
import {DirectusTranslation} from '@frontend/directus';
import {DirectusTooltipComponent} from '../../../../../../../../shared/components/directus-tooltip/directus-tooltip.component';
import {SumFees} from '../step-8-2-3-rfq-preview-price-list.types';
import {FormFieldNumberComponent} from '../../../../../../../../shared/components/inputs/form-field/form-field-number/form-field-number.component';
import {AbstractFormArrayDirective} from '../../../step-8-1-proposal-parameter/components/abstract-form-array-directive';
import {startWith} from 'rxjs';
import {FormFieldSelectButtonComponent} from '../../../../../../../../shared/components/inputs/form-field/form-field-submit-button/form-field-select-button.component';
import {NoDataComponent} from '../../../../../../../../shared/components/no-data/no-data.component';
import {NgClass} from '@angular/common';
import {PanelHeaderComponent} from '../../../../../../../../shared/components/header/panel-header/panel-header.component';
import {Panel} from 'primeng/panel';
import {RfqFeeCategoryService} from '../../../../../../../../shared/services/rfq-fee-category.service';

interface FeeRowVM {
  data: IntegrationSetupFeeResponse;
  totalAcrossScope: number;
}

@Component({
  selector: 'app-integration-setup-fees-module',
  imports: [
    ReactiveFormsModule,
    ProjectStepSaveButtonComponent,
    TableModule,
    TranslocoPipe,
    CommentModalButtonComponent,
    AppCurrencyPipe,
    TranslateDirectusPipe,
    DirectusTooltipComponent,
    FormFieldNumberComponent,
    FormFieldSelectButtonComponent,
    NoDataComponent,
    NgClass,
    PanelHeaderComponent,
    Panel,
  ],
  templateUrl: './integration-setup-fees.component.html',
  providers: [TranslateDirectusPipe],
})
export class IntegrationSetupFeesComponent extends AbstractFormArrayDirective<
  IntegrationSetupFeesContainer,
  IntegrationSetupFeeResponse
> {
  protected readonly feeCategories = inject(RfqFeeCategoryService);
  private static readonly FORM_CONTROLS: FormControlsConfig<IntegrationSetupFeesContainer> = {
    locale: [''],
    currencyCode: [''],
    scopeYears: [[]],
    data: [[]],
  };
  readonly isCustomerType = input<boolean>(true);

  readonly dataDomainOptions = toSignal(this.directusService.loadDirectusDropdown('data-domain'), {
    initialValue: [],
  });

  readonly integrationTypeOptions = toSignal(
    this.directusService.loadDirectusDropdown('integration-type'),
    {
      initialValue: [],
    },
  );

  readonly integrationComplexityOptions = toSignal(
    this.directusService.loadDirectusDropdown('integration_complexity'),
    {
      initialValue: [],
    },
  );

  private formValue = toSignal(this.theForm.valueChanges.pipe(startWith(this.theForm.value)));

  readonly feeRows = computed<FeeRowVM[]>(() => {
    this.formValue();
    const current = this.inputObject();
    const formData = this.theForm.get('data')?.value ?? [];
    if (!current?.data?.length) return [];

    return current.data.map((item, index) => {
      const formRow = formData[index];
      const formYearlyFees = formRow?.yearlyItemFees ?? item.yearlyItemFees;
      const formDirectusComplexityUuid =
        formRow?.directusComplexityUuid ?? item.directusComplexityUuid;

      return {
        data: {
          uniqueIdentifier: item.uniqueIdentifier,
          fromName: item.fromName,
          toName: item.toName,
          directusDataDomainUuid: item.directusDataDomainUuid,
          directusIntegrationTypeUuid: item.directusIntegrationTypeUuid,
          directusComplexityUuid: formDirectusComplexityUuid,
          officesLookupResponse: item.officesLookupResponse,
          yearlyItemFees: formYearlyFees,
          comment: formRow?.comment ?? item.comment,
        },
        totalAcrossScope: formYearlyFees.reduce(
          (sum: number, fee: any) => sum + (Number(fee.feePerUnit) || 0),
          0,
        ),
      } as FeeRowVM;
    });
  });

  readonly columnTotalsPerYear = computed<number[]>(() =>
    this.inputObject().scopeYears.map((_, yearIndex) =>
      this.feeRows().reduce(
        (sum, row) => sum + (Number(row.data.yearlyItemFees[yearIndex]?.feePerUnit) || 0),
        0,
      ),
    ),
  );

  readonly grandTotalAllYears = computed<number>(() =>
    this.feeRows().reduce((sum, row) => sum + row.totalAcrossScope, 0),
  );

  readonly sumFees = computed<SumFees>(() => {
    const columnTotalsPerYear = this.columnTotalsPerYear();
    const grandTotalAllYears = this.grandTotalAllYears();
    return {
      columnTotalsPerYear: columnTotalsPerYear,
      grandTotalAllYears: grandTotalAllYears,
    } as SumFees;
  });

  readonly sumFeesModel = model<SumFees>({} as SumFees);

  constructor() {
    super(IntegrationSetupFeesComponent.FORM_CONTROLS);

    effect(() => {
      this.sumFeesModel.set(this.sumFees());
    });
  }

  protected createRowFormGroup(item: IntegrationSetupFeeResponse): FormGroup {
    return this.fb.group({
      uniqueIdentifier: [item.uniqueIdentifier],
      directusComplexityUuid: [item.directusComplexityUuid],
      yearlyItemFees: this.fb.array(
        item.yearlyItemFees.map((fee) =>
          this.fb.group({
            index: [fee.index],
            feePerUnit: [fee.feePerUnit],
          }),
        ),
      ),
      comment: [item.comment],
    });
  }

  protected getDataDomain(uuid: string): DirectusTranslation[] {
    const option = this.dataDomainOptions().find((x) => x.id === uuid);

    return option?.translations ?? [];
  }

  protected getIntegrationType(uuid: string): DirectusTranslation[] {
    const option = this.integrationTypeOptions().find((x) => x.id === uuid);

    return option?.translations ?? [];
  }
}
